import React, { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import posts from '../posts.generated.json';
import { formatDate } from '../lib/format';
import '../styles/home.css';
import '../styles/blog.css';

function BlogPost() {
  const { slug } = useParams();
  const post = posts.find((p) => p.slug === slug);

  // Title is set here rather than in a helmet library — one page, one string.
  useEffect(() => {
    if (!post) return;
    const previous = document.title;
    document.title = `${post.title} · Zenbul`;
    return () => {
      document.title = previous;
    };
  }, [post]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const index = posts.findIndex((p) => p.slug === slug);
  const next = posts[index + 1]; // older

  return (
    <>
      <Nav />
      {/* One column wrapper for article, next-link, and CTA so every horizontal
          rule aligns to the same text measure. */}
      <main className="post wrap post-shell">
        <article className="post-inner">
          <Link to="/blog" className="post-back">
            <span aria-hidden="true">&larr;</span> All posts
          </Link>

          <div className="post-meta">
            <time dateTime={post.date}>{formatDate(post.date)}</time>
            <span aria-hidden="true">&middot;</span>
            <span>{post.readingMinutes} min read</span>
            <span aria-hidden="true">&middot;</span>
            <span>{post.author}</span>
          </div>

          <h1 className="post-title">{post.title}</h1>
          {post.summary && <p className="post-summary">{post.summary}</p>}

          {post.tags.length > 0 && (
            <ul className="post-tags">
              {post.tags.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          )}

          <div className="post-body">
            {/* No rehype-raw: HTML in a post is escaped, not rendered, so a
                stray tag can't break the page layout. */}
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ node, href, children, ...props }) => {
                  const external = /^https?:\/\//i.test(href || '');
                  return (
                    <a
                      href={href}
                      {...(external
                        ? { target: '_blank', rel: 'noopener noreferrer' }
                        : {})}
                      {...props}
                    >
                      {children}
                    </a>
                  );
                },
              }}
            >
              {post.body}
            </ReactMarkdown>
          </div>
        </article>

        {next && (
          <div className="post-next">
            <p className="eyebrow">Next</p>
            <Link to={`/blog/${next.slug}`} className="post-next-link">
              {next.title} <span aria-hidden="true">&rarr;</span>
            </Link>
          </div>
        )}

        <div className="post-cta">
          <h2 className="post-cta-title">Noise blocking your next project?</h2>
          <p className="post-cta-lead">
            Send us your site data. Design and specs back within a week.
          </p>
          <Link to="/#assessment" className="btn btn--primary">
            Request a site assessment
          </Link>
        </div>
      </main>
      <Footer />
    </>
  );
}

export default BlogPost;
